import { MapPin, CalendarDays, Trees, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { EvidenceChip } from "@/components/Evidence";

export default function TreePlantation() {
  return (
    <>
      <Seo title="450 Trees, One Field Day · Project Dawn" path="/dawn/tree-plantation" description="On 28 February 2024, volunteers under Project Dawn planted 450 trees in Village Kuna, Tiuni, Uttarakhand. A documented field note." />
      <PageHero
        eyebrow="Field note · February 2024"
        titleLines={["450 trees.", "One field day."]}
        intro="A documented tree-planting drive under Project Dawn, uniting volunteers for a greener tomorrow in Village Kuna, Tiuni, Uttarakhand."
        image="/dawn/trees-kids.jpg"
      />

      <section className="bg-ptf-bg py-14 md:py-20 border-b border-ptf-primary/10">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12 flex flex-wrap items-center gap-x-12 gap-y-6" data-testid="trees-facts">
          <div>
            <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ptf-text/50">Trees planted</p>
            <p className="font-display uppercase text-3xl tracking-tight text-ptf-primary mt-1 inline-flex items-center gap-2"><Trees className="h-5 w-5 text-ptf-secondary" /> 450</p>
          </div>
          <div>
            <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ptf-text/50">Date</p>
            <p className="font-display uppercase text-3xl tracking-tight text-ptf-primary mt-1 inline-flex items-center gap-2"><CalendarDays className="h-5 w-5 text-ptf-secondary" /> 28 Feb 2024</p>
          </div>
          <div>
            <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ptf-text/50">Location</p>
            <p className="font-display uppercase text-3xl tracking-tight text-ptf-primary mt-1 inline-flex items-center gap-2"><MapPin className="h-5 w-5 text-ptf-secondary" /> Kuna, Tiuni</p>
          </div>
          <EvidenceChip status="DOCUMENTED" className="ml-auto" />
        </div>
      </section>

      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <MaskText
              lines={["Seeding a", "greener future."]}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
            />
            <Reveal delay={0.15}>
              <p className="mt-8 text-lg text-ptf-text/80 leading-relaxed">
                On 28 February 2024, under Project Dawn, volunteers planted 450 trees
                in Village Kuna of Tiuni, near Dehradun in Uttarakhand. The drive
                united passionate volunteers for a sustainable impact in the area:
                one field day, recorded plant by plant.
              </p>
              <p className="mt-6 text-ptf-text/70 leading-relaxed">
                The site sits at 30°55'26.2"N 77°51'47.6"E. School children joined the
                planting, which doubled as environmental education: the same principle
                Project Dawn applies everywhere: participation first, then impact.
              </p>
              <p className="mt-6 text-sm text-ptf-text/55 leading-relaxed">
                Follow-up: tree survival rates have not yet been published. They will
                appear here once documented.
              </p>
              <a
                href="https://maps.google.com/?q=30.923944,77.863222"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="trees-map-link"
                className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.16em] text-ptf-primary hover:text-ptf-secondary transition-colors"
              >
                Open the geo-location <ArrowUpRight className="h-4 w-4" />
              </a>
            </Reveal>
          </div>
          <div className="lg:col-span-5 space-y-4">
            <Reveal><img src="/dawn/trees-planting.jpg" alt="Planting and measuring on the Kuna hillside" className="w-full aspect-[4/3] object-cover" loading="lazy" /></Reveal>
            <Reveal delay={0.1}><img src="/dawn/trees-field.jpg" alt="The plantation field at Kuna, Tiuni" className="w-full aspect-[4/3] object-cover" loading="lazy" /></Reveal>
            <Reveal delay={0.15}><img src="/dawn/trees-kids.jpg" alt="School children planting a sapling" className="w-full aspect-[4/3] object-cover" loading="lazy" /></Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ptf-primary text-ptf-bg py-20 md:py-28 grain">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <h2 className="font-display font-extrabold uppercase text-4xl md:text-5xl tracking-tight leading-[0.95]">The next drive needs<br />hands and saplings.</h2>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <PtfButton to="/volunteer" variant="accent" data-testid="trees-volunteer-cta">Volunteer for a Drive</PtfButton>
            <PtfButton to="/dawn" variant="outlineLight">Back to Project Dawn</PtfButton>
          </div>
        </div>
      </section>
    </>
  );
}
